import { AddressInput } from '@commenda/kakashi';
import type { IAddress } from '@commenda/types';
import { useContext } from 'react';
import { FormContext } from '../../hooks/FormContext';
import { isRuleActive } from '../../utils/validation';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

const defaultCountryFallback = 'US';

function withDefaultCountry(
  addr: Record<string, unknown>,
  defaultCountry?: string
): Record<string, unknown> {
  const country = addr?.country;
  const hasCountry = typeof country === 'string' && country.trim() !== '';
  return {
    ...addr,
    country: hasCountry ? country : (defaultCountry || defaultCountryFallback),
  };
}

/** Normalize loose form state into IAddress for kakashi AddressInput. */
function recordToIAddress(
  addr: Record<string, unknown>,
  countryDefault: string
): IAddress {
  const s = (key: string): string =>
    typeof addr[key] === 'string' ? (addr[key] as string) : '';
  const line2 = s('addressLine2');
  const countryRaw = s('country').trim();
  return {
    addressLine1: s('addressLine1'),
    ...(line2 ? { addressLine2: line2 } : {}),
    postalCode: s('postalCode'),
    city: s('city'),
    state: s('state'),
    country: countryRaw || countryDefault || defaultCountryFallback,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AddressInputComponent = ({
  fieldId,
  label,
  description,
  guidance,
  validation,
  props = {},
}: any) => {
  const { formData, updateField, validateField, isFieldVisible, errors } = useContext(FormContext);
  const raw = formData[fieldId];
  // keyPerson locations: value shape is { locationType, address }; props.locationType from filters
  const locationType = props.locationType as string | undefined;
  const defaultCountry = (props.defaultCountry as string) || defaultCountryFallback;
  const isLocationsShape = locationType && raw && typeof raw === 'object' && !Array.isArray(raw) && 'address' in raw;
  const address = isLocationsShape
    ? (raw.address as Record<string, unknown>) || {}
    : (raw as Record<string, unknown>) || {};
  const addressForInput = recordToIAddress(
    withDefaultCountry(address, defaultCountry),
    defaultCountry
  );
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  const handleChange = (addr: IAddress) => {
    const addrObj = { ...addr } as Record<string, unknown>;
    const normalizedAddr = withDefaultCountry(addrObj, defaultCountry);
    if (locationType) {
      const next = { ...(typeof raw === 'object' && raw && !Array.isArray(raw) ? raw : {}), locationType, address: normalizedAddr };
      updateField(fieldId, next);
      validateField(fieldId, next, validation?.rules);
    } else {
      updateField(fieldId, normalizedAddr);
      validateField(fieldId, normalizedAddr, validation?.rules);
    }
  };

  return (
    <div className="mb-4">
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          description={description}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
      )}
      <AddressInput
        address={addressForInput}
        onChange={handleChange}
        defaultCountry={props.defaultCountry}
      />
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}
    </div>
  );
};
